"use client";

import {motion} from "framer-motion";
import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {ChatView} from "@/components/chat";
import {
   type OptimisticMessage,
   useChatTransfer,
} from "@/contexts/common/ChatTransferContextProvider";
import {Permissions, RoleData} from "@/enums/user";
import {useAlert} from "@/hooks/useAlert";
import {useBanner} from "@/hooks/useBanner";
import {useChat} from "@/hooks/useChat";
import {useOrg} from "@/hooks/useOrg";
import {usePermissions} from "@/hooks/usePermissions";
import {useUserProfile} from "@/hooks/useUserProfile";
import {useSkills} from "@/hooks/useSkills";
import ChatSkeleton from "./ChatSkeleton";
import ChatWorkflowPanel from "./ChatWorkflowPanel";
import {useConversationModelChange} from "./useConversationModelChange";
import {useExternalChatSends} from "./useExternalChatSends";
import {useWorkflowEditorBinding} from "@/contexts/chat/WorkflowEditorBridgeContext";

interface ChatViewContainerProps {
   orgId?: string;
   conversationId: string;
   /** Fill the parent element instead of viewport-height layout (dock mode). */
   fillContainer?: boolean;
}

const getOptimisticMessage = (payload: {message: OptimisticMessage}): OptimisticMessage => ({
   text: payload.message.text,
   fileData: payload.message.fileData || [],
   assistantId: payload.message.assistantId,
});

const ChatViewContainer = ({orgId, conversationId, fillContainer}: ChatViewContainerProps) => {
   const {isBannerActive} = useBanner();
   const {setAlert} = useAlert();
   const {ui} = useOrg();
   const {skills} = useSkills();
   const {userProfile} = useUserProfile();
   const {canAccess} = usePermissions();
   const {t} = useTranslation("ask");
   const {t: ta} = useTranslation("assistants");
   const canEditAssistants = canAccess(RoleData.Assistants, Permissions.Update);

   const {consumeOptimisticTransfer, peekOptimisticTransfer} = useChatTransfer();
   const pendingOptimisticTransfer = peekOptimisticTransfer(conversationId);
   const initialOptimisticTransferRef = useRef(pendingOptimisticTransfer);

   const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>();
   const [isLoadingConversation, setIsLoadingConversation] = useState(!pendingOptimisticTransfer);
   const [conversationAssistantId, setConversationAssistantId] = useState<string | null>(
      () => pendingOptimisticTransfer?.assistantId ?? null
   );
   const [optimisticMessage, setOptimisticMessage] = useState<OptimisticMessage | null>(() =>
      pendingOptimisticTransfer ? getOptimisticMessage(pendingOptimisticTransfer) : null
   );
   // Beside the builder, the open workflow is the one this chat edits.
   const workflowEditor = useWorkflowEditorBinding();

   const {
      messages,
      handleWorkflowTest,
      handleWorkflowDebug,
      workflowRecovery,
      workflowError,
      reconcileWorkflowHistory,
      workflowToolsAvailable,
      workflowPresentation,
      inputValue,
      setInputValue,
      isSubmitting,
      streamingMessageId,
      selectedAssistant,
      isLoadingAssistants,
      displayAssistants,
      displaySelectedAssistant,
      handleAssistantChange,
      updateAssistantData,
      handleSendMessage,
      handleToolApprovalResponse,
      handleFilesSelected,
      handleFilesChange,
      handleInvalidFiles,
      attachedFiles,
      fileUploadConfig,
      convertToChatMessages,
      loadExistingConversation,
      canSendMessages,
      handleFeedback,
      cancelStream,
      tokenLimitReached,
      tokenUsagePercent,
      tokenBreakdown,
      streamingStatus,
   } = useChat({
      orgId,
      conversationId,
      initialAssistantId: conversationAssistantId,
      optimisticMessage,
      attachPageContext: fillContainer,
      selectedWorkflowId: workflowEditor?.workflowId ?? selectedWorkflowId,
      prepareWorkflowTurn: workflowEditor?.prepareTurn,
   });

   const {changeModel: handleModelChange, isSaving: isModelSaving} = useConversationModelChange({
      conversationId,
      onModelUpdated: (assistantId, model) => updateAssistantData(assistantId, {model}),
   });

   useExternalChatSends({
      conversationId,
      fillContainer,
      canSend: !isLoadingConversation && !isLoadingAssistants && !isSubmitting && canSendMessages &&
         !!selectedAssistant && selectedAssistant.id !== "loading",
      tokenLimitReached,
      sendMessage: handleSendMessage,
      setInputValue,
   });

   // Memoize converted messages to avoid re-processing on every keystroke
   const convertedMessages = useMemo(
      () => convertToChatMessages(messages),
      [messages, convertToChatMessages]
   );
   const latestMessage = messages.at(-1);
   const activeToolApprovalMessageId = latestMessage?.role === "assistant" && !latestMessage.synthetic
      ? latestMessage.id : undefined;

   // Check for optimistic conversation data from in-memory transfer
   useEffect(() => {
      const payload = consumeOptimisticTransfer(conversationId);

      // The first-render transfer already seeded state. Only write state when
      // a later conversation supplies a different transfer; replacing the
      // seeded message would needlessly replay useChat's send effect.
      if (payload && payload !== initialOptimisticTransferRef.current) {
         setOptimisticMessage(getOptimisticMessage(payload));

         setConversationAssistantId(payload.assistantId);
         setIsLoadingConversation(false);
      }
   }, [conversationId, consumeOptimisticTransfer]);

   useEffect(() => {
      // An optimistic first message needs no history, and streamed messages are already current.
      if (optimisticMessage) return;
      if (messages.length > 0) {
         setIsLoadingConversation(false);
         return;
      }

      const reportLoadFailure = (...details: unknown[]) => {
         console.error(...details);
         setAlert({status: "error", message: t("failedToLoadConversation"), secondsToClose: 10});
      };
      setIsLoadingConversation(true);
      loadExistingConversation()
         .then((result) => {
            if (result?.conversation) setConversationAssistantId(result.conversation.assistantId);
            else reportLoadFailure("Failed to load conversation details - cannot proceed with loading messages");
         })
         .catch((error) => reportLoadFailure("Error initializing conversation:", error))
         .finally(() => setIsLoadingConversation(false));
   }, [conversationId, orgId, optimisticMessage]);

   if (!isLoadingAssistants && (!selectedAssistant || selectedAssistant.id === "loading")) {
      return (
         <div className="flex justify-center items-center h-full">
            <div className="text-center">
               <h3 className="mb-2 font-semibold text-lg">{ta("noAssistants")}</h3>
               <p className="text-gray-600">{ta("noChatHistory")}</p>
            </div>
         </div>
      );
   }
   // Show loading only if we're not in optimistic mode and haven't loaded optimistic data yet
   if (isLoadingConversation && !optimisticMessage) {
      return (
         <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.3}}
            className="h-full"
         >
            <ChatSkeleton fillContainer={fillContainer} />
         </motion.div>
      );
   }

   let disabled = !canSendMessages || selectedAssistant?.status !== "active" || tokenLimitReached;

   let inputPlaceholder = t("reply");
   if (disabled && tokenLimitReached) {
      inputPlaceholder = t("tokenLimitReached");
   } else if (disabled) {
      inputPlaceholder = t("cannotSendMessages");
   }

   return (
      <motion.div
         initial={{opacity: 0, y: 6}}
         animate={{opacity: 1, x: 0, y: 0}}
         transition={{duration: 0.35, ease: [0.16, 1, 0.3, 1], delay: 0.05}}
         className="h-full"
      >
         <ChatView
            workflowPanel={workflowToolsAvailable ? (
               <ChatWorkflowPanel
                  presentation={workflowPresentation}
                  conversationId={conversationId}
                  messages={messages}
                  historyLoaded={!isLoadingConversation}
                  editor={workflowEditor}
                  selectedWorkflowId={selectedWorkflowId}
                  onSelectWorkflow={setSelectedWorkflowId}
                  assistantId={selectedAssistant?.id}
                  disabled={disabled}
                  busy={isSubmitting || streamingMessageId !== null}
                  recovery={workflowRecovery}
                  generationError={workflowError}
                  onRecover={reconcileWorkflowHistory}
                  onTest={handleWorkflowTest}
                  onDebug={handleWorkflowDebug}
               />
            ) : undefined}
            skills={skills}
            conversationId={conversationId}
            messages={convertedMessages}
            inputValue={inputValue}
            onInputChange={setInputValue}
            onSendMessage={handleSendMessage}
            onToolApprovalResponse={handleToolApprovalResponse}
            activeToolApprovalMessageId={activeToolApprovalMessageId}
            isSubmitting={isSubmitting}
            onCancelStream={cancelStream}
            disabled={disabled || workflowRecovery === "reconciling" || workflowRecovery === "blocked"}
            isBannerActive={isBannerActive}
            senderName={selectedAssistant?.name ?? "Assistant"}
            assistants={displayAssistants}
            selectedAssistant={displaySelectedAssistant || undefined}
            onAssistantChange={isLoadingAssistants ? () => {} : handleAssistantChange}
            inputPlaceholder={inputPlaceholder}
            brand={ui.brand}
            theme={userProfile.theme}
            onFilesSelected={handleFilesSelected}
            attachedFiles={attachedFiles}
            onFilesChange={handleFilesChange}
            fileUploadConfig={fileUploadConfig}
            onInvalidFiles={handleInvalidFiles}
            isLoadingAssistants={isLoadingAssistants}
            fileCompatibilityTooltip={t("assistantDoesNotSupportFilesTooltip")}
            isStreaming={streamingMessageId !== null}
            streamingStatus={streamingStatus}
            onFeedback={handleFeedback}
            canEditModel={canEditAssistants}
            onModelChange={handleModelChange}
            isModelSaving={isModelSaving}
            tokenLimitReached={tokenLimitReached}
            tokenUsagePercent={tokenUsagePercent}
            tokenBreakdown={tokenBreakdown}
            fillContainer={fillContainer}
         />
      </motion.div>
   );
};

export default ChatViewContainer;
